import { BatteryFull, ChevronLeft, CirclePlus, Signal, Wifi } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { cn } from "@/lib/cn";
import { PHONE_HOME, PHONE_TABS, phoneChrome } from "./phoneSurface";
import { useViewport } from "./ViewportProvider";

/**
 * M12 — the phone app: a status bar, a 52 px header, the scrolling body, and five tabs.
 *
 * No rail, no navigator panel and no pipeline breadcrumb, exactly as the handoff says. The
 * desktop layout does not survive contact with a 390 px viewport, so none of it is scaled
 * down to fit: what the rail and the panel become is a tab bar with five destinations and a
 * back button, and everything the tab bar cannot reach is named as desk work rather than
 * half-ported (`phoneSurface.ts`).
 *
 * Both shells render the same `<Outlet/>` from the same route table. Whether a route draws
 * its phone screen or its desktop one is decided one level up, in the router — so the shell
 * here never has to know which page is inside it.
 */
export function PhoneShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { framed } = useViewport();
  const { reviewCounts, worldView } = useApp();

  const chrome = phoneChrome(pathname);

  // The handoff's `mAddFrom`: Add a source is entered from World view or from Needs you and
  // its back button returns to whichever it was. Held as a ref rather than state because
  // nothing renders differently for it — it is only read when the button is pressed.
  const lastTab = useRef(PHONE_HOME);

  useEffect(() => {
    const tab = PHONE_TABS.find((candidate) => candidate.id === chrome.tab);
    if (tab) lastTab.current = tab.path;
  }, [chrome.tab]);

  // A subject page is titled by its subject, which the shell can name from the index it
  // already holds. On a cold deep link that index has not arrived yet, and "Subject" is the
  // honest thing to say for the moment before it does — the same rule the desktop
  // breadcrumb follows.
  const title = useMemo(() => {
    if (chrome.title !== null) return chrome.title;

    const slug = pathname.split("/")[2];
    return worldView?.subjects.find((subject) => subject.slug === slug)?.title ?? "Subject";
  }, [chrome.title, pathname, worldView]);

  // What the tab actually leads to, which is the merged stream and not the Review queue's
  // half of it (M13). The phone app is the simple-mode surface, so its badge is simple
  // mode's number — a badge promising less than the screen holds sends somebody to a queue
  // that does not end when the number says it should.
  const waiting = reviewCounts?.needsYou ?? 0;

  const shell = (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--surface-0)] text-[var(--text-primary)]",
        framed && "h-full",
      )}
    >
      {framed && <MockStatusBar />}

      <header
        className="flex h-[52px] shrink-0 items-center gap-1.5 border-b border-[var(--border-subtle)] pl-4 pr-2.5"
        // Outside the preview frame the notch is real and the header has to clear it. The
        // 52 px is the header itself either way; the inset is added above it.
        style={framed ? undefined : { height: "auto", minHeight: 52, paddingTop: "env(safe-area-inset-top)" }}
      >
        {chrome.back && (
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(chrome.back === "last-tab" ? lastTab.current : chrome.back!)}
            className="-ml-3 flex size-11 shrink-0 items-center justify-center text-[var(--text-secondary)]"
          >
            <ChevronLeft size={24} strokeWidth={2} aria-hidden />
          </button>
        )}

        <h1 className="min-w-0 flex-1 truncate text-[17px] font-extrabold tracking-[var(--tracking-snug)]">
          {title}
        </h1>

        {chrome.showAdd && (
          <button
            type="button"
            aria-label="Add a source"
            onClick={() => navigate("/add")}
            className="flex size-11 shrink-0 items-center justify-center text-[var(--vl-life-soft)]"
          >
            <CirclePlus size={24} strokeWidth={2} aria-hidden />
          </button>
        )}
      </header>

      {/* No scrollbar rather than the app's thin one: this is the one surface where the
          scrollbar is the operating system's to draw. `phone-scroll` is in index.css
          because hiding the WebKit pseudo-element needs a real rule. */}
      <div className="phone-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <Outlet />
      </div>

      <nav
        aria-label="Phone navigation"
        className="flex shrink-0 items-stretch border-t border-[var(--border-subtle)] bg-[var(--surface-1)]"
        // 16 px in the handoff's frame; on a real phone it is whatever the home indicator
        // actually needs, which on a device without one is nothing.
        style={{ paddingBottom: framed ? 16 : "max(8px, env(safe-area-inset-bottom))" }}
      >
        {PHONE_TABS.map((tab) => {
          const active = chrome.tab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-[3px]",
                active ? "text-[var(--primary)]" : "text-[var(--text-muted)]",
              )}
            >
              <span className="relative inline-flex">
                <Icon size={22} strokeWidth={2} aria-hidden />
                {tab.badged && waiting > 0 && (
                  <span className="absolute -right-2 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-[var(--radius-full)] bg-[var(--primary)] px-1 text-[9px] font-bold text-[var(--primary-foreground)]">
                    {waiting}
                  </span>
                )}
              </span>
              <span className={cn("text-[10px]", active ? "font-bold" : "font-medium")}>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );

  if (!framed) return shell;

  return (
    <div className="flex h-full items-center justify-center bg-[var(--sidebar)] p-[var(--space-4)]">
      <div
        className="flex h-[min(844px,100%)] w-[390px] shrink-0 flex-col overflow-hidden border border-[var(--border-strong)] shadow-[var(--shadow-xl)]"
        style={{ borderRadius: 40 }}
      >
        {shell}
      </div>
    </div>
  );
}

/**
 * The handoff's 38 px status bar, and it exists **only** inside the preview frame. On a
 * real phone the operating system draws this strip, and a second one reading 9:41 would be
 * a lie the app tells about the time in the one place a user always knows the answer.
 */
function MockStatusBar() {
  return (
    <div
      aria-hidden
      className="flex h-[38px] shrink-0 items-center justify-between px-6 text-[12px] font-bold text-[var(--text-secondary)]"
    >
      <span>9:41</span>
      <span className="inline-flex items-center gap-[5px] text-[var(--text-muted)]">
        <Signal size={12} strokeWidth={2} />
        <Wifi size={12} strokeWidth={2} />
        <BatteryFull size={16} strokeWidth={2} />
      </span>
    </div>
  );
}
